import { type Epic, getClaudeAssessment, getEpicOverride, getBatchClaudeAssessments, getBatchEpicOverrides } from './review-data';

export interface EstimateResult {
  estimate: number;
  estimateSource: 'D' | 'M';
}

/**
 * Get the estimate source badge label based on data_source
 */
export function getEstimateSourceBadge(_epic: Epic): 'Dur' {
  return 'Dur';
}

/**
 * Get the tooltip text for estimate source
 */
export function getEstimateSourceTooltip(epic: Epic, _allEpics: Epic[]): string {
  if (epic.active_date && epic.closed_date) {
    const activeDate = new Date(epic.active_date);
    const closedDate = new Date(epic.closed_date);
    const days = Math.round((closedDate.getTime() - activeDate.getTime()) / (1000 * 60 * 60 * 24));
    return `Duration: ${days} days between Active and Closed dates`;
  }
  return 'Duration (days) — fallback to 1.0 when dates unavailable';
}

export interface PercentageResult {
  activeCapExPercentage: number;
  activeRnDPercentage: number;
  capExSource: 'M' | 'C' | 'A';
  rndSource: 'M' | 'C' | 'A';
  claudeCapExPercentage: number;
  claudeRnDPercentage: number;
  // Estimate override fields
  activeEstimate: number | null;
  estimateOverrideSource: 'D' | 'M' | null;
}

/**
 * Calculate the estimate for an EPIC using duration_created_to_closed.
 * Duration is the objective, audit-defensible weight for EPIC sizing.
 * Fallback to 1.0 when duration is not available.
 */
export async function calculateEpicEstimate(
  epic: Epic,
  _allEpics: Epic[]
): Promise<EstimateResult> {
  const duration = calculateDuration(epic.active_date, epic.closed_date);
  return {
    estimate: duration > 0 ? duration : 1,
    estimateSource: 'D',
  };
}

/**
 * Calculate duration in days between two dates
 */
function calculateDuration(activeDate: string | null, closedDate: string | null): number {
  if (!activeDate || !closedDate) return 0;

  try {
    const start = new Date(activeDate);
    const end = new Date(closedDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch {
    return 0;
  }
}

/**
 * Determine the active CapEx and R&D percentages using the hierarchy:
 * 1. Manual override (epic_overrides)
 * 2. Higher of Claude assessment or ALM data (for each percentage independently)
 * 3. Default to 0%
 */
export async function calculateActivePercentages(
  apiUrl: string,
  epic: Epic,
  reviewId: string
): Promise<PercentageResult> {
  const override = await getEpicOverride(apiUrl, epic.id, reviewId);
  const claudeAssessment = await getClaudeAssessment(apiUrl, epic.id);
  const almCapEx = epic.capex_percentage ?? 0;
  const almRnD = epic.r_and_d_percentage ?? 0;
  const claudeCapEx = claudeAssessment?.capex_percentage ?? 0;
  const claudeRnD = claudeAssessment?.r_and_d_percentage ?? 0;

  // Determine CapEx percentage and source
  let activeCapExPercentage: number;
  let capExSource: 'M' | 'C' | 'A';
  
  if (override?.capex_source === 'manual') {
    activeCapExPercentage = override.capex_percentage;
    capExSource = 'M';
  } else if (override?.capex_source === 'claude') {
    activeCapExPercentage = claudeCapEx;
    capExSource = 'C';
  } else if (override?.capex_source === 'alm') {
    activeCapExPercentage = almCapEx;
    capExSource = 'A';
  } else {
    // No override - use the higher of Claude or ALM
    if (claudeCapEx >= almCapEx) {
      activeCapExPercentage = claudeCapEx;
      capExSource = 'C';
    } else {
      activeCapExPercentage = almCapEx;
      capExSource = 'A';
    }
  }

  // Determine R&D percentage and source
  let activeRnDPercentage: number;
  let rndSource: 'M' | 'C' | 'A';
  
  if (override?.r_and_d_source === 'manual') {
    activeRnDPercentage = override.r_and_d_percentage;
    rndSource = 'M';
  } else if (override?.r_and_d_source === 'claude') {
    activeRnDPercentage = claudeRnD;
    rndSource = 'C';
  } else if (override?.r_and_d_source === 'alm') {
    activeRnDPercentage = almRnD;
    rndSource = 'A';
  } else {
    // No override - use the higher of Claude or ALM
    if (claudeRnD >= almRnD) {
      activeRnDPercentage = claudeRnD;
      rndSource = 'C';
    } else {
      activeRnDPercentage = almRnD;
      rndSource = 'A';
    }
  }

  return {
    activeCapExPercentage,
    activeRnDPercentage,
    capExSource,
    rndSource,
    claudeCapExPercentage: claudeCapEx,
    claudeRnDPercentage: claudeRnD,
    activeEstimate: null, // TODO: Implement estimate calculation if needed
    estimateOverrideSource: null, // TODO: Implement estimate source if needed
  };
}

/**
 * Batch calculate active percentages for multiple EPICs
 * This is much more efficient than calling calculateActivePercentages for each EPIC individually
 */
export async function calculateBatchActivePercentages(
  apiUrl: string,
  epics: Epic[],
  reviewId: string
): Promise<Map<string, PercentageResult>> {
  const epicIds = epics.map(e => e.id);
  
  // Fetch all overrides and assessments in parallel
  const [overridesMap, assessmentsMap] = await Promise.all([
    getBatchEpicOverrides(apiUrl, epicIds, reviewId),
    getBatchClaudeAssessments(apiUrl, epicIds),
  ]);

  const resultsMap = new Map<string, PercentageResult>();

  for (const epic of epics) {
    const override = overridesMap.get(epic.id);
    const claudeAssessment = assessmentsMap.get(epic.id);
    const almCapEx = epic.capex_percentage ?? 0;
    const almRnD = epic.r_and_d_percentage ?? 0;
    const claudeCapEx = claudeAssessment?.capex_percentage ?? 0;
    const claudeRnD = claudeAssessment?.r_and_d_percentage ?? 0;

    // Determine CapEx percentage and source
    let activeCapExPercentage: number;
    let capExSource: 'M' | 'C' | 'A';
    
    if (override?.capex_source === 'manual') {
      // User explicitly chose manual override for CapEx
      activeCapExPercentage = override.capex_percentage;
      capExSource = 'M';
    } else if (override?.capex_source === 'claude') {
      // User explicitly chose Claude for CapEx
      activeCapExPercentage = claudeCapEx;
      capExSource = 'C';
    } else if (override?.capex_source === 'alm') {
      // User explicitly chose ALM for CapEx
      activeCapExPercentage = almCapEx;
      capExSource = 'A';
    } else {
      // No override - use the higher of Claude or ALM
      if (claudeCapEx >= almCapEx) {
        activeCapExPercentage = claudeCapEx;
        capExSource = 'C';
      } else {
        activeCapExPercentage = almCapEx;
        capExSource = 'A';
      }
    }

    // Determine R&D percentage and source
    let activeRnDPercentage: number;
    let rndSource: 'M' | 'C' | 'A';
    
    if (override?.r_and_d_source === 'manual') {
      // User explicitly chose manual override for R&D
      activeRnDPercentage = override.r_and_d_percentage;
      rndSource = 'M';
    } else if (override?.r_and_d_source === 'claude') {
      // User explicitly chose Claude for R&D
      activeRnDPercentage = claudeRnD;
      rndSource = 'C';
    } else if (override?.r_and_d_source === 'alm') {
      // User explicitly chose ALM for R&D
      activeRnDPercentage = almRnD;
      rndSource = 'A';
    } else {
      // No override - use the higher of Claude or ALM
      if (claudeRnD >= almRnD) {
        activeRnDPercentage = claudeRnD;
        rndSource = 'C';
      } else {
        activeRnDPercentage = almRnD;
        rndSource = 'A';
      }
    }

    // Determine estimate override — duration is the default weight, manual is the only override
    let activeEstimate: number | null = null;
    let estimateOverrideSource: 'D' | 'M' | null = null;
    
    if (override?.estimate_source === 'manual' && override?.estimate_value !== null && override?.estimate_value !== undefined) {
      activeEstimate = override.estimate_value;
      estimateOverrideSource = 'M';
    } else {
      // Default to duration
      if (epic.active_date && epic.closed_date) {
        const start = new Date(epic.active_date);
        const end = new Date(epic.closed_date);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          activeEstimate = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        }
      }
      estimateOverrideSource = 'D';
    }

    resultsMap.set(epic.id, {
      activeCapExPercentage,
      activeRnDPercentage,
      capExSource,
      rndSource,
      claudeCapExPercentage: claudeCapEx,
      claudeRnDPercentage: claudeRnD,
      activeEstimate,
      estimateOverrideSource,
    });
  }

  return resultsMap;
}

/**
 * Calculate weighted averages for CapEx and R&D percentages
 */
export function calculateWeightedAverages(
  epics: Array<{
    estimate: number;
    activeCapExPercentage: number;
    activeRnDPercentage: number;
  }>
): { weightedCapEx: number; weightedRnD: number } {
  if (epics.length === 0) {
    return { weightedCapEx: 0, weightedRnD: 0 };
  }

  const totalWeight = epics.reduce((sum, epic) => sum + epic.estimate, 0);

  if (totalWeight === 0) {
    return { weightedCapEx: 0, weightedRnD: 0 };
  }

  const weightedCapExSum = epics.reduce(
    (sum, epic) => sum + (epic.estimate * epic.activeCapExPercentage) / 100,
    0
  );

  const weightedRnDSum = epics.reduce(
    (sum, epic) => sum + (epic.estimate * epic.activeRnDPercentage) / 100,
    0
  );

  const weightedCapEx = (weightedCapExSum / totalWeight) * 100;
  const weightedRnD = (weightedRnDSum / totalWeight) * 100;

  return { weightedCapEx, weightedRnD };
}
